import { useMemo, useState } from 'react';
import StoriesLib from 'stories-react';
import { useModalStore } from '@/entities/Modal';
import { classNames } from '@/shared/lib/classNames';
import Arrow from '@/shared/assets/lk/arrow.svg?react';
import { ModalWrapper } from '@/shared/ui/ModalWrapper';
import CloseIcon from '@/shared/assets/lk/closeIcon.svg?react';
import { useResize } from '@/shared/lib/hooks/useResize/useResize';
import { Story } from '../model/types';
import { SliderButton } from './SliderButton';
import cls from './StoriesModal.module.scss';

type StoryComponent = {
    component: () => JSX.Element;
    duration: number;
    type: 'component';
};

type Props = {
    stories: Story[];
};

type Direction = 'backward' | 'forward';

const DIRECTION_INDEXES: Record<Direction, number> = {
    forward: 1,
    backward: -1,
};

export const StoriesModal = ({ stories }: Props) => {
    const { isStoriesModalOpen, setIsStoriesModalOpen } = useModalStore();

    const [currentStory, setCurrentStory] = useState(0);

    const preparedStories: StoryComponent[] = useMemo(() => {
        setCurrentStory(0);

        return stories.map((story) => {
            return {
                type: 'component',
                duration: 5000,
                component: () => {
                    const { description, title, backgroundImage } = story;

                    return (
                        <div className={cls.storyContainer}>
                            <img alt="storyBg" src={backgroundImage} />
                            <div className={cls.info}>
                                <p className={cls.title}>{title}</p>
                                <p className={cls.description}>{description}</p>
                            </div>
                        </div>
                    );
                },
            };
        });
    }, [isStoriesModalOpen, stories]);

    const onSwipeButtonClick = (direction: Direction) => {
        setCurrentStory(currentStory + DIRECTION_INDEXES[direction]);
    };

    const onStoryChange = (id: number) => {
        setCurrentStory(id);
    };

    const width = useResize();
    const isControlsShown = width >= 768;

    const isPrevButtonShown = isControlsShown && currentStory !== 0;
    const isNextButtonShown = isControlsShown && currentStory !== stories.length - 1;

    return (
        <ModalWrapper
            className="overflowHidden"
            hasBody={false}
            id="storiesModal"
            isOpened={isStoriesModalOpen}
            setIsOpened={setIsStoriesModalOpen}
        >
            <SliderButton className={cls.closeButton} icon={<CloseIcon />} onClick={() => setIsStoriesModalOpen(false)} />
            {isPrevButtonShown && (
                <SliderButton className={cls.prevSlideButton} icon={<Arrow />} onClick={() => onSwipeButtonClick('backward')} />
            )}
            {isNextButtonShown && (
                <SliderButton className={cls.nextSlideButton} icon={<Arrow />} onClick={() => onSwipeButtonClick('forward')} />
            )}
            <div className={classNames(cls.container, {}, ['storiesContainer'])}>
                <StoriesLib
                    stories={preparedStories}
                    currentIndex={currentStory}
                    onStoryChange={onStoryChange}
                    onAllStoriesEnd={() => setIsStoriesModalOpen(false)}
                />
            </div>
        </ModalWrapper>
    );
};
